import { useEffect, useRef } from 'react';

const steps = [
  {
    num: '01',
    label: 'Signal',
    icon: 'ri-radar-line',
    title: 'Identify Operational Signals',
    description: 'We audit your existing workflows, data flows, and communication gaps to identify where operational intelligence is failing or absent.',
  },
  {
    num: '02',
    label: 'Structure',
    icon: 'ri-layout-grid-line',
    title: 'Architect the System',
    description: 'We design a precise operational architecture — intake flows, qualification logic, CRM structure, and automation sequences — engineered for your environment.',
  },
  {
    num: '03',
    label: 'Deploy',
    icon: 'ri-rocket-line',
    title: 'Deploy Infrastructure',
    description: 'We build and integrate the system into your existing stack with zero operational disruption. All components are tested, documented, and handed over.',
  },
  {
    num: '04',
    label: 'Control',
    icon: 'ri-dashboard-line',
    title: 'Establish Operational Control',
    description: 'With monitoring dashboards and execution protocols in place, your team operates with full visibility and command over every critical process.',
  },
];

export default function ProcessSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('[data-animate]');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateY(0)';
              }, i * 130);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" className="bg-white dark:bg-[#0B0B0C] py-28 md:py-40 border-t border-[#E8E8ED] dark:border-[#252528]">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={sectionRef}>
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-24">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#5BBFB5]" />
            <span className="text-[#C9A840] text-xs font-semibold" style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
              How We Operate
            </span>
            <span className="w-8 h-px bg-[#5BBFB5]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1D1D1F] dark:text-[#F2F0EC] tracking-tight leading-tight mb-6">
            A Systematic Approach to<br />
            <span className="text-[#C9A840]">Intelligence Deployment</span>
          </h2>
          <p className="text-[#6E6E73] dark:text-[#96969E] text-lg leading-relaxed">
            Every engagement follows a defined four-phase process that moves from
            discovery to full operational deployment with no ambiguity at any stage.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <div
              key={step.num}
              data-animate
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                transition: `opacity 0.7s ease ${i * 0.13}s, transform 0.7s ease ${i * 0.13}s`,
              }}
              className="group bg-[#FBFBFD] dark:bg-[#111113] border border-[#E8E8ED] dark:border-[#252528] hover:border-[#C9A840]/40 dark:hover:border-[#C9A840]/30 p-8 relative overflow-hidden transition-all duration-300"
            >
              {/* Top gold accent */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-[#C9A840] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

              {/* Large faded number */}
              <div className="absolute -bottom-4 -right-2 text-[80px] font-black text-[#C9A840]/5 leading-none select-none">
                {step.num}
              </div>

              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center border border-[#E8E8ED] dark:border-[#252528] group-hover:border-[#C9A840]/50 mb-7 transition-colors duration-300 relative z-10">
                <i className={`${step.icon} text-[#C9A840] text-xl`}></i>
                {/* Teal circuit dot */}
                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#5BBFB5]/40 group-hover:bg-[#5BBFB5]/80 transition-colors duration-300" />
              </div>

              <span className="block text-[#C9A840] text-xs font-bold mb-3 relative z-10" style={{ letterSpacing: '0.22em', textTransform: 'uppercase' }}>
                {step.label}
              </span>

              <h3 className="text-[#1D1D1F] dark:text-[#F2F0EC] text-lg font-bold tracking-tight mb-4 relative z-10 leading-snug">
                {step.title}
              </h3>

              <p className="text-[#6E6E73] dark:text-[#96969E] text-sm leading-relaxed relative z-10">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Signal flow bar */}
        <div className="mt-16 hidden md:flex items-center justify-center">
          {steps.map((step, i) => (
            <div key={step.num} className="flex items-center">
              <div className="px-8 py-3 border border-[#E8E8ED] dark:border-[#252528] text-[#6E6E73] dark:text-[#96969E] text-xs" style={{ letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                {step.label}
              </div>
              {i < steps.length - 1 && (
                <div className="flex items-center">
                  <div className="w-10 h-px bg-[#C9A840]/40" />
                  <i className="ri-arrow-right-s-line text-[#C9A840]/50 text-sm" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
